import { apiUrl } from '@algoan/dashboard/env';
import { AsyncThunk, Dispatch } from '@algoan/dashboard/store/store';
import { PaginatedResources, Transaction } from '@algoan/dashboard/model';
import {
  fetchTransactionsFailure,
  fetchTransactionsStart,
  fetchTransactionsSuccess,
  updateTransactionComment,
} from '@algoan/dashboard/store/slice';
import { buildUrl, monitoring } from '@algoan/dashboard/utils';
import { authRequest } from './auth-thunks';
import { analysisPageCancellationToken } from './dashboard-thunks';

export const fetchTransactions =
  (customerId: string, analysisId: string) =>
  async (dispatch: Dispatch): Promise<Transaction[] | undefined> => {
    try {
      const url = buildUrl(`${apiUrl}/v2/customers/${customerId}/analyses/${analysisId}/transactions`, [
        { key: 'page', value: 1 },
        { key: 'limit', value: 500 },
      ]);

      dispatch(fetchTransactionsStart());
      const transactions = await dispatch(recursiveFetchTransactions(url, []));
      if (transactions.length === 0) {
        dispatch(fetchTransactionsFailure({ error: 'noTransactionsError' }));
        return;
      }

      dispatch(fetchTransactionsSuccess(transactions));
      return transactions;
    } catch (error) {
      dispatch(fetchTransactionsFailure({ error: 'genericError' }));
    }
    return undefined;
  };
const recursiveFetchTransactions =
  (nextUrl: string, transactions: Transaction[]) =>
  async (dispatch: Dispatch): Promise<Transaction[]> => {
    const { data: response } = await dispatch(
      authRequest<PaginatedResources<Transaction>>('GET', nextUrl, undefined, {
        cancelToken: analysisPageCancellationToken?.token,
      }),
    );
    const { pagination, resources } = response;

    const accumulatedTransactions = [...transactions, ...resources];
    return pagination.next
      ? dispatch(recursiveFetchTransactions(`${apiUrl}${pagination.next}`, accumulatedTransactions))
      : accumulatedTransactions;
  };

const patchTransactionComment = async (
  dispatch: Dispatch,
  comment: string,
  customerId: string,
  analysisId: string | undefined,
  accountId: string,
  transactionId: string,
  previousComment: string,
): Promise<void> => {
  if (analysisId === undefined) {
    return;
  }

  const URL = `${apiUrl}/v2/customers/${customerId}/analyses/${analysisId}/accounts/${accountId}/transactions/${transactionId}`;

  try {
    dispatch(updateTransactionComment({ id: transactionId, comment: comment }));
    await dispatch(authRequest<Transaction>('patch', URL, { comment }));

    return;
  } catch (error) {
    dispatch(updateTransactionComment({ id: transactionId, comment: previousComment }));
    throw error;
  }
};

export const editTransactionComment =
  (
    statement: {
      accountId: string;
      id: string;
      analysisId: string | undefined;
      customerId: string;
      previousComment: string;
    },
    comment: string,
  ): AsyncThunk =>
  async (dispatch: Dispatch): Promise<void> => {
    const logError = (error: Error): void =>
      monitoring.logError({
        error,
        fingerprint: 'invalid_team_state',
        message: 'User does not belong to an organisation with teams, but has a team on their JWT token',
        context: { level: 'error' },
      });

    return patchTransactionComment(
      dispatch,
      comment,
      statement.customerId,
      statement.analysisId,
      statement.accountId,
      statement.id,
      statement.previousComment,
    ).catch((error) => {
      logError(error);
      return Promise.reject(error);
    });
  };
